import React, { forwardRef } from 'react';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import Board from '@atlaskit/icon/glyph/board';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import PreferencesIcon from '@atlaskit/icon/glyph/preferences';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type Props = {
	isLoading?: boolean;
	isSelected?: boolean;
	onClick?: ButtonProps['onClick'];
};

const PickerButtonOld = forwardRef<HTMLElement, Props>(
	({ onClick, isLoading = false, isSelected = false }: Props, ref) => {
		const { formatMessage } = useIntl();

		return (
			<Tooltip position="bottom" content={formatMessage(messages.tooltipText)} delay={0}>
				<Container>
					<Button
						appearance="subtle"
						testId="issue-table-column-picker.ui.column-picker.picker-button.picker-button"
						aria-label={formatMessage(messages.columnPickerButtonText)}
						iconAfter={
							<Wrapper>
								<ChevronDownIcon
									label={formatMessage(messages.chevronDownIconText)}
									size="medium"
								/>
							</Wrapper>
						}
						iconBefore={<Board label={formatMessage(messages.boardIconText)} size="medium" />}
						isDisabled={isLoading}
						onClick={onClick}
						ref={ref}
						spacing="none"
						aria-haspopup="true"
						aria-expanded={isSelected}
						isSelected={isSelected}
					/>
				</Container>
			</Tooltip>
		);
	},
);

const PickerButton = forwardRef<HTMLButtonElement, Props>(
	({ onClick, isLoading = false, isSelected = false }: Props, ref) => {
		const { formatMessage } = useIntl();

		const iconColor = isSelected
			? token('color.icon.selected', '#0C66E4')
			: token('color.icon', '#44546F');

		return (
			<Tooltip position="bottom" content={formatMessage(messages.tooltipText)} delay={0}>
				<Button
					appearance="subtle"
					testId="issue-table-column-picker.ui.column-picker.picker-button.picker-button"
					aria-label={formatMessage(messages.tooltipText)}
					iconBefore={<PreferencesIcon label="" size="medium" primaryColor={iconColor} />}
					isDisabled={isLoading}
					onClick={onClick}
					ref={ref}
					aria-haspopup="true"
					aria-expanded={isSelected}
					isSelected={isSelected}
				/>
			</Tooltip>
		);
	},
);

export const PickerButtonFallback = () => {
	const { formatMessage } = useIntl();

	if (!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
		return (
			<Container>
				<Button
					appearance="subtle"
					aria-label={formatMessage(messages.columnPickerButtonText)}
					iconAfter={
						<Wrapper>
							<ChevronDownIcon label={formatMessage(messages.chevronDownIconText)} size="medium" />
						</Wrapper>
					}
					iconBefore={<Board label={formatMessage(messages.boardIconText)} size="medium" />}
					isDisabled
					spacing="none"
				/>
			</Container>
		);
	}

	return (
		<Button
			appearance="subtle"
			aria-label={formatMessage(messages.tooltipText)}
			iconBefore={<PreferencesIcon label="" size="medium" />}
			isDisabled
		/>
	);
};

export default componentWithFF(
	'jsv-one-issue-table-spreadsheet-ui_ygrnf',
	PickerButton,
	PickerButtonOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.span({
	margin: token('space.negative.050', '-4px'),
});

// using div instead of composition styled(button) because innerRef is needed instead of ref, but this causes troubles with placement of picker popup
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		padding: token('space.050', '4px'),
		// fix focus outline for tight spaces
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		outlineOffset: '0 !important',
	},
});
